import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map, Observable} from 'rxjs';
import {BaseService} from './base.service';
import {ApiResponse} from '../model/api-response';
import {Event} from '../model/event';

@Injectable({
  providedIn: 'root',
})
export class EventService extends BaseService {
  httpClient: HttpClient = inject(HttpClient);

  constructor() {
    super();
  }

  getAllEvents(includePast: boolean = false): Observable<ApiResponse<Array<Event>>> {
    const params = new HttpParams().set('includePast', includePast);
    return this.httpClient
      .get<ApiResponse<Array<Event>>>(`${this.baseUrl}/events`, { params })
      .pipe(map((response) => this.normalizeEventArrayResponse(response)));
  }

  getMyEvents(): Observable<ApiResponse<Array<Event>>> {
    return this.httpClient
      .get<ApiResponse<Array<Event>>>(`${this.baseUrl}/events/mine`)
      .pipe(map((response) => this.normalizeEventArrayResponse(response)));
  }

  getEventById(id: number): Observable<ApiResponse<Event>> {
    const params = new HttpParams().set('_t', new Date().getTime().toString());
    return this.httpClient
      .get<ApiResponse<Event>>(`${this.baseUrl}/events/${id}`, { params })
      .pipe(map((response) => this.normalizeEventResponse(response)));
  }

  createEvent(event: Event): Observable<ApiResponse<Event>> {
    return this.httpClient.post<ApiResponse<Event>>(`${this.baseUrl}/events`, event);
  }

  updateEvent(event: Event): Observable<ApiResponse<Event>> {
    return this.httpClient.put<ApiResponse<Event>>(`${this.baseUrl}/events/update`, event);
  }

  deleteEvent(id: number): Observable<ApiResponse<string>> {
    return this.httpClient.delete<ApiResponse<string>>(`${this.baseUrl}/events/${id}`);
  }

  addInvitedOrganization(eventId: number, orgId: number): Observable<ApiResponse<Event>> {
    return this.httpClient.post<ApiResponse<Event>>(`${this.baseUrl}/events/${eventId}/invited-organizations/${orgId}`, null)
      .pipe(map((response) => this.normalizeEventResponse(response)));
  }

  removeInvitedOrganization(eventId: number, orgId: number): Observable<ApiResponse<Event>> {
    return this.httpClient.delete<ApiResponse<Event>>(`${this.baseUrl}/events/${eventId}/invited-organizations/${orgId}`)
      .pipe(map((response) => this.normalizeEventResponse(response)));
  }

  private normalizeEventArrayResponse(response: ApiResponse<Array<Event>>): ApiResponse<Array<Event>> {
    if (!response.data) {
      return response;
    }

    return {
      ...response,
      data: response.data.map((event) => this.normalizeEventDates(event)),
    };
  }

  private normalizeEventResponse(response: ApiResponse<Event>): ApiResponse<Event> {
    if (!response.data) {
      return response;
    }

    return {
      ...response,
      data: this.normalizeEventDates(response.data),
    };
  }

  private normalizeEventDates(event: Event): Event {
    return {
      ...event,
      startDate: this.normalizeDateString(event.startDate) ?? event.startDate,
      endDate: this.normalizeDateString(event.endDate),
    };
  }

  private normalizeDateString(value?: string): string | undefined {
    if (!value) {
      return undefined;
    }

    const dateTimeMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})$/);
    if (dateTimeMatch) {
      const [, day, month, year] = dateTimeMatch;
      return `${year}-${month}-${day}`;
    }

    const dateOnlyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dateOnlyMatch) {
      const [, day, month, year] = dateOnlyMatch;
      return `${year}-${month}-${day}`;
    }

    return value;
  }
}
